package com.flowyride.data.geocoding.photon

import com.flowyride.core.domain.geocoding.Geocoder
import com.flowyride.core.domain.geocoding.GeocodingException
import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.model.Place
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.http.isSuccess
import kotlin.coroutines.cancellation.CancellationException

/** [Geocoder] backed by Photon (OpenStreetMap data, free, CORS enabled). */
class PhotonGeocoder(
    private val client: HttpClient,
    private val config: PhotonConfig = PhotonConfig(),
) : Geocoder {

    override suspend fun search(query: String, limit: Int): List<Place> =
        // Photon often returns the same place several times (city node, boundary, POIs named after it).
        call("api") {
            parameter("q", query)
            parameter("limit", limit)
        }
            .features
            .mapNotNull { it.toPlaceOrNull() }
            .distinctBy { it.name to it.detail }

    override suspend fun reverse(point: GeoPoint): Place? =
        call("reverse") {
            parameter("lat", point.latitude)
            parameter("lon", point.longitude)
            parameter("limit", 1)
        }
            .features
            .firstNotNullOfOrNull { it.toPickedPlaceOrNull() }
            ?.copy(point = point)

    private suspend fun call(endpoint: String, block: HttpRequestBuilder.() -> Unit): PhotonResponse {
        val response = try {
            client.get("${config.baseUrl.trimEnd('/')}/$endpoint") {
                parameter("lang", config.language)
                block()
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            throw GeocodingException("Place search unreachable: ${e.message}", e)
        }
        if (!response.status.isSuccess()) {
            throw GeocodingException("Place search failed (HTTP ${response.status.value})")
        }
        return try {
            response.body<PhotonResponse>()
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            throw GeocodingException("Unexpected place search response", e)
        }
    }
}
